import { bunja } from 'bunja';
import { addMinutes } from 'date-fns';
import { atom } from 'jotai';
import { atomWithDefault, selectAtom } from 'jotai/utils';

import { Timestamp } from '@flipster/apis/types';
import { triggerPriceTypeEnum, type TriggerPriceType } from '@flipster/apis/v2/trade/types';
import { leverageRestrictionBunja } from '@flipster/features/src/margin-bonus/leverage-restriction.state';
import { authBunja } from '@flipster/features/src/shared/auth/state';
import { getEffectiveIsolatedMax, getModeMaxLeverage } from '@flipster/features/src/shared/leverage/isolated-cap';
import { JotaiStoreScope } from '@flipster/utils/bunja-jotai';
import {
  calcAmountToMargin,
  calcAmountToQty,
  calcMarginToAmount,
  calcMarginToQty,
  calcQtyToAmount,
  calcQtyToMargin,
} from '@flipster/utils/calculator/order-preview';
import { UsdtPrecision } from '@flipster/utils/constants/currency';
import { dateToEpochString } from '@flipster/utils/datetime';
import { getFlipsterConfigsV2Key, getLocalStorage, setLocalStorage } from '@flipster/utils/localStorage';
import Num, { BigNumber, numberFormatter } from '@flipster/utils/num';
import segment from '@flipster/utils/segment';
import { TradeEvent } from '@flipster/utils/segment/events/trade';

import { tradeUnitBunja } from '@web/app/[locale]/(web)/trade/_misc/state';

import { commonFormBunja, type PriceType } from '../../common';
import { isLegActive, shouldResetTpSlOnPositionModeChange, type AdvancedTpSlPreset } from '../advanced-tpsl';
import { migrateTradeDefaultsCache } from '../migrate-trade-defaults-cache';
import {
  marginTypeBunja,
  oneWayPositionLeverageBunja,
  perpAccountBunja,
  resolveTriggerReferencePrice,
  symbolBunja,
} from './base';
import { clampFormLeverageOnMaxChange } from './logic/derivation/leverage';
import { isEffectiveReduceOnly } from './logic/derivation/reduce-only';
import { type SlippageMode } from './slippage';
import type { ValidationResult } from './validation';

interface MarginState {
  kind: 'margin';
  leverage: number;
  margin: string;
}
interface QuantityState {
  kind: 'quantity';
  leverage: number;
  quantity: string;
}
interface AmountState {
  kind: 'amount';
  leverage: number;
  amount: string;
}
export type OrderState = MarginState | QuantityState | AmountState;

export const InitValues = {
  leverage: 1,
};

type Side = 'Long' | 'Short' | 'unknown';
/**
 * Trigger 주문에서 트리거 발동 후 체결 방식.
 * - `'market'`: StopMarket (기존 동작 유지)
 * - `'limit'`: StopLimit — limit price(또는 BBO)로 호가 등록
 */
export type TriggerExecutionType = 'market' | 'limit';
export const formUserInputBunja = bunja(() => {
  /** 주문 시 어떤 방향 포지션을 잡았는지 기록 */
  const sideAtom = atom<Side>('unknown');
  const triggerPriceAtom = atom<string | undefined>(undefined);
  const limitPriceAtom = atom<string | undefined>(undefined);
  const isBboAtom = atom(false);
  const triggerExecutionTypeAtom = atom<TriggerExecutionType>('market');
  const triggerPriceTypeAtom = atom<TriggerPriceType>(triggerPriceTypeEnum.last);

  return { sideAtom, triggerPriceAtom, limitPriceAtom, isBboAtom, triggerExecutionTypeAtom, triggerPriceTypeAtom };
});
formUserInputBunja.debugLabel = 'formUserInputBunja';

export const formBunja = bunja(() => {
  const store = bunja.use(JotaiStoreScope);
  const { userAtom } = bunja.use(authBunja);
  const { symbol, symbolInfoAtom, referencePriceAtom, referencePriceReady, markPriceAtom } = bunja.use(symbolBunja);
  const { availableFundsAtom, isOneWayAtom } = bunja.use(perpAccountBunja);
  const { marginTypeAtom } = bunja.use(marginTypeBunja);
  const { priceTypeAtom } = bunja.use(commonFormBunja);

  // selectAtom keeps clamp effect off price-tick churn; modeMax still reacts to margin mode + caps.
  const crossMaxAtom = selectAtom(symbolInfoAtom, (info) => info.maxLeverageHardLimit);
  const isolatedCapAtom = selectAtom(symbolInfoAtom, (info) => info.maxInitLeverageIsolatedMargin);
  const modeMaxLeverageAtom = atom((get) => {
    const crossMax = get(crossMaxAtom);
    const effectiveIsolatedMax = getEffectiveIsolatedMax(crossMax, get(isolatedCapAtom));
    return getModeMaxLeverage(get(marginTypeAtom), crossMax, effectiveIsolatedMax);
  });

  const { hasOneWayPositionAtom, positionLeverageAtom, currentSymbolPositionAtom, currentSymbolPositionSideAtom } =
    bunja.use(oneWayPositionLeverageBunja);
  const { leverageRestrictionAtom } = bunja.use(leverageRestrictionBunja);
  const { tradeUnitAtom, changeTradeUnit } = bunja.use(tradeUnitBunja);

  const { sideAtom, triggerPriceAtom, limitPriceAtom, isBboAtom, triggerExecutionTypeAtom, triggerPriceTypeAtom } =
    bunja.use(formUserInputBunja);
  const shouldShowBracketErrorAtom = atom(false);

  /**
   * triggerTimestamp 현재 시간 + 2분 buffer
   * https://prextech.slack.com/archives/C087D884VEE/p1738892295896349?thread_ts=1738658286.700269&cid=C087D884VEE
   */
  const triggerTimestampAtom = atom<Timestamp>(dateToEpochString(addMinutes(new Date(), 2)));
  /** trigger type (Trigger price or Trigger time) */
  const triggerTypeAtom = atom<'price' | 'time'>('price');
  const triggerReferencePriceAtom = atom((get) =>
    resolveTriggerReferencePrice(get(triggerPriceTypeAtom), {
      lastPrice: get(referencePriceAtom),
      markPrice: get(markPriceAtom),
    }),
  );
  /** 선택한 trigger price type 기준가로 trigger price 값을 채움 */
  async function resetTriggerPrice() {
    await referencePriceReady;
    const referencePrice = store.get(triggerReferencePriceAtom);
    const precision = store.get(symbolInfoAtom).pricePrecision;
    store.set(dirtyAtom, (prev) => ({ ...prev, triggerPrice: false }));
    store.set(
      triggerPriceAtom,
      numberFormatter(referencePrice, { method: 'round-half-up', precision, withComma: false }),
    );
  }

  async function resetLimitPrice() {
    await referencePriceReady;
    const referencePrice = store.get(referencePriceAtom);
    const precision = store.get(symbolInfoAtom).pricePrecision;
    store.set(
      limitPriceAtom,
      numberFormatter(referencePrice, { method: 'round-half-up', precision, withComma: false }),
    );
  }

  /** 현재시간 + 2분으로 trigger timestamp 값을 채움 */
  const resetTriggerTimestampAtom = atom(null, (_, set) => {
    set(dirtyAtom, (prev) => ({ ...prev, triggerTimestamp: false }));
    set(triggerTimestampAtom, dateToEpochString(addMinutes(new Date(), 2)));
  });
  /** trigger price 아래의 percent 버튼에 따라 값을 변경 */
  const handleTriggerPricePercentAtom = atom(null, (get, set, value: string) => {
    const referencePrice = get(triggerReferencePriceAtom);
    const triggerPrice = get(triggerPriceAtom) || '0';
    const { pricePrecision } = get(symbolInfoAtom);
    const triggerPriceNum = Num(triggerPrice).isEqualTo(0) ? Num(referencePrice) : Num(triggerPrice);
    const sumPrice = triggerPriceNum.div(100).times(value);
    const next = triggerPriceNum.plus(sumPrice).toFixed(pricePrecision, BigNumber.ROUND_HALF_UP);
    segment.track(TradeEvent.SelectPercentageClick, {
      symbol,
      trade_type: 'perpetual',
      percentage: Num(value).dividedBy(100).toNumber(),
      percentage_type: 'trigger_price',
    });
    set(triggerPriceAtom, next);
  });
  /**
   * 실제로 유저가 입력 가능한 값
   * @description
   * - kind 'margin': 유저가 margin 탭에서 margin 입력을 보고 있음
   * - kind 'quantity': 유저가 positionSize 탭에서 quantity 입력을 보고 있음
   * - kind 'amount': 유저가 positionSize 탭에서 amount 입력을 보고 있음
   * */

  const orderStateAtom = atomWithDefault<OrderState>((get) => {
    const initialQtyPrecision = get(symbolInfoAtom)?.tradeQtyPrecision ?? 2;
    return { kind: 'quantity', leverage: InitValues.leverage, quantity: Num('0').toFixed(initialQtyPrecision) };
  });
  /** leverage 표시 전용 필드 selector — orderState의 수량 keystroke 변경에는 반응하지 않는다 */
  const leverageAtom = atom((get) => get(orderStateAtom).leverage);
  /** kind(margin/quantity/amount) 전용 필드 selector — 입력값 keystroke 변경에는 반응하지 않는다.
   *  헤더 드롭다운·입력부 분기 등 kind에만 의존하는 UI가 값 변경마다 리렌더되지 않도록 한다. */
  const kindAtom = atom((get) => get(orderStateAtom).kind);
  /**
   * 최근 수정한 입력에서만 에러를 보여주도록 하기 위한 상태
   */
  const dirtyAtom = atom({
    leverage: false,
    margin: false,
    quantity: false,
    amount: false,
    orderPercent: false,
    triggerTimestamp: false,
    triggerPrice: false,
  });

  const orderTypeFormFieldErrorAtom = atom<null | { field: 'quantity' | 'amount' | 'margin'; message: string }>(null);

  function checkValidationAll() {
    store.set(dirtyAtom, {
      leverage: true,
      margin: true,
      quantity: true,
      amount: true,
      orderPercent: true,
      triggerTimestamp: true,
      triggerPrice: true,
    });
  }
  function resetDirty() {
    store.set(dirtyAtom, {
      leverage: false,
      margin: false,
      quantity: false,
      amount: false,
      orderPercent: false,
      triggerTimestamp: false,
      triggerPrice: false,
    });
    store.set(orderTypeFormFieldErrorAtom, null);
  }
  const recentOrderErrorsAtom = atom<ValidationResult>({});
  /** 변환 시에는 정확한 값이 필요없으므로 throttle reference price를 사용 */
  const calculationPriceAtom = atom((get) => {
    const priceType = get(priceTypeAtom);
    const referencePrice = get(referencePriceAtom);
    switch (priceType) {
      case 'market':
        return referencePrice;
      case 'trigger': {
        const triggerType = get(triggerTypeAtom);
        if (triggerType === 'time') return referencePrice;
        // Trigger Limit: 모든 계산은 limit price 기준 (BBO 시 reference price)
        const executionType = get(triggerExecutionTypeAtom);
        if (executionType === 'limit') {
          const isBbo = get(isBboAtom);
          if (isBbo) return referencePrice;
          const limitPrice = get(limitPriceAtom);
          return limitPrice || referencePrice;
        }
        // Trigger Market: 기존 동작 유지 (trigger price 기준)
        const triggerPrice = get(triggerPriceAtom);
        return triggerPrice || referencePrice;
      }
      case 'limit': {
        const isBbo = get(isBboAtom);
        const limitPrice = get(limitPriceAtom);
        if (isBbo) return referencePrice;
        return limitPrice || referencePrice;
      }
    }
  });
  const tpSlAtom = atom<{
    tp?: string;
    sl?: string;
    tpTriggerPriceType: TriggerPriceType;
    slTriggerPriceType: TriggerPriceType;
    /** tpSl 체크박스가 눌러져 있는지 (withTpSl) */
    enabled: boolean;
  }>({
    enabled: false,
    tpTriggerPriceType: triggerPriceTypeEnum.last,
    slTriggerPriceType: triggerPriceTypeEnum.last,
  });
  const updateTpSlAtom = atom(
    null,
    (
      _,
      set,
      action:
        | { type: 'enabled'; value: boolean }
        | { type: 'tp' | 'sl'; value: string }
        | { type: 'tpTriggerPriceType' | 'slTriggerPriceType'; value: TriggerPriceType },
    ) => {
      set(recentOrderErrorsAtom, {});
      const { type, value } = action;
      if (type === 'enabled') set(tpSlAtom, (tpSl) => ({ ...tpSl, enabled: value }));
      else set(tpSlAtom, (tpSl) => ({ ...tpSl, [type]: value }));
    },
  );

  const focusedTpSlFieldAtom = atom<'tp' | 'sl' | null>(null);

  type EstPnlResult = { pnl: string; roi: string } | null;
  type EstPnlSides = { long: EstPnlResult; short: EstPnlResult };
  const tpslEstPnlAtom = atom<{ tp: EstPnlSides; sl: EstPnlSides } | null>((get) => {
    const tpSl = get(tpSlAtom);
    if (!tpSl.enabled) return null;

    const tradePrice = get(calculationPriceAtom);
    const orderState = get(orderStateAtom);
    const symbolInfo = get(symbolInfoAtom);
    const { leverage } = orderState;
    const value = getKindValue(orderState);
    const converted = convert(leverage, tradePrice, { quantityPrecision: symbolInfo.tradeQtyPrecision })[
      orderState.kind
    ](value);
    const qty = converted.quantity;
    const margin = converted.margin;

    const calcPnl = (triggerPrice: string | undefined): EstPnlSides => {
      if (
        !triggerPrice ||
        Num(triggerPrice).isZero() ||
        Num(qty).isZero() ||
        Num(tradePrice).isZero() ||
        Num(margin).isZero()
      ) {
        return { long: null, short: null };
      }

      const longPnl = Num(triggerPrice).minus(tradePrice).times(qty);
      const shortPnl = Num(tradePrice).minus(triggerPrice).times(qty);
      const longRoi = longPnl.div(margin).times(100);
      const shortRoi = shortPnl.div(margin).times(100);

      return {
        long: { pnl: longPnl.toString(), roi: longRoi.toString() },
        short: { pnl: shortPnl.toString(), roi: shortRoi.toString() },
      };
    };

    return {
      tp: calcPnl(tpSl.tp),
      sl: calcPnl(tpSl.sl),
    };
  });

  // 오더북 클릭 등으로 TP/SL이 자동 채워질 때 발사되는 신호. (커서 위치 보정 등 후처리에 사용)
  const tpSlAutofillSignalAtom = atom<{ field: 'tp' | 'sl'; nonce: number } | null>(null);
  const autofillTpSlAtom = atom(null, (get, set, { type, value }: { type: 'tp' | 'sl'; value: string }) => {
    set(updateTpSlAtom, { type, value });
    const prev = get(tpSlAutofillSignalAtom);
    set(tpSlAutofillSignalAtom, { field: type, nonce: (prev?.nonce ?? 0) + 1 });
  });

  const tpslAllowStatusAtom = atom<'allow' | 'disallow' | 'not-supported'>((get) => {
    const isOneWay = get(isOneWayAtom);
    const priceType = get(priceTypeAtom);
    const triggerType = get(triggerTypeAtom);
    const isPriceTrigger = priceType === 'trigger' && triggerType === 'price';
    const isTimeTrigger = priceType === 'trigger' && triggerType === 'time';
    const reduceOnly = get(reduceOnlyAtom);

    if (isOneWay) {
      if (priceType === 'market') return reduceOnly ? 'disallow' : 'allow';
      if (isPriceTrigger) return reduceOnly ? 'disallow' : 'allow';
      if (isTimeTrigger) return 'not-supported';
      if (priceType === 'limit') return reduceOnly ? 'disallow' : 'allow';
    } else {
      // multi position
      if (priceType === 'market') return 'allow';
      if (isPriceTrigger) return 'allow';
      if (isTimeTrigger) return 'not-supported';
      if (priceType === 'limit') return 'allow';
    }

    return 'disallow';
  });

  const slippageEnabledBaseAtom = atomWithDefault<boolean>(
    () => getLocalStorage('flipster.configs.v1.marketOrderSlippageEnabled') ?? false,
  );
  const slippageEnabledAtom = atom(
    (get) => get(slippageEnabledBaseAtom),
    (_, set, next: boolean) => {
      setLocalStorage('flipster.configs.v1.marketOrderSlippageEnabled', next);
      set(slippageEnabledBaseAtom, next);
    },
  );
  const slippageModeAtom = atom<SlippageMode>('percent');
  const slippageValueAtom = atom('');
  // By-price needs the submit-time side + crossing-side BBO to build `BBO ± spread`, which aren't
  // in the form state until the user presses Long/Short — so the request fields are built inline
  // at submit in `action.tsx` (via `buildSlippageRequestFields`), not in a derived atom here.
  // Reset after every submit (ERD-233). The checkbox stays checked per symbol (Figma); only the
  // per-order value is cleared and the mode falls back to the default ('percent').
  function resetSlippage() {
    store.set(slippageModeAtom, 'percent');
    store.set(slippageValueAtom, '');
  }

  const isOrderPriceOrAmountEmptyAtom = atom((get) =>
    isOrderPriceOrAmountEmpty({
      orderState: get(orderStateAtom),
      priceType: get(priceTypeAtom),
      triggerType: get(triggerTypeAtom),
      triggerExecutionType: get(triggerExecutionTypeAtom),
      isBbo: get(isBboAtom),
      limitPrice: get(limitPriceAtom),
      triggerPrice: get(triggerPriceAtom),
    }),
  );

  /** 확정(commit)된 Advanced TP/SL preset. null = 미설정. 심볼 변경 시 formBunja 재생성으로 자동 초기화. */
  const advancedTpSlPresetAtom = atom<AdvancedTpSlPreset | null>(null);

  const commitAdvancedTpSlAtom = atom(null, (_, set, preset: AdvancedTpSlPreset) => {
    set(advancedTpSlPresetAtom, preset);
  });

  const clearAdvancedTpSlAtom = atom(null, (_, set) => {
    set(advancedTpSlPresetAtom, null);
  });

  /**
   * 설정해 둔 TP/SL을 전부 초기화한다 (인라인 체크 해제·trigger 비움 + Advanced preset 제거).
   * 레버리지 변경(QA-20882)·포지션 모드 전환(QA-20888) 등 청산가·유효성 전반이 달라지는
   * 상황에서 공통으로 사용해 재입력을 유도한다.
   */
  const resetTpSlAtom = atom(null, (_, set) => {
    set(tpSlAtom, {
      enabled: false,
      tpTriggerPriceType: triggerPriceTypeEnum.last,
      slTriggerPriceType: triggerPriceTypeEnum.last,
    });
    set(advancedTpSlPresetAtom, null);
  });

  /**
   * Advanced TP/SL이 설정 완료되었는지 여부.
   * 설정이 완료되면 진입 버튼이 Advanced → Edit으로 전환되고, 인라인 입력 대신 per-leg summary가 노출된다.
   */
  const isAdvancedTpSlConfiguredAtom = atom((get) => {
    const preset = get(advancedTpSlPresetAtom);
    return preset != null && (isLegActive(preset.tp) || isLegActive(preset.sl));
  });

  const reduceOnlyAtom = atom<boolean>(false);

  /**
   * 클라 검증에서 reduce-only로 취급할지 여부 (명시 플래그 || one-way 반대 방향 감소 주문).
   * 검증 캡(leverage/notional) 스킵에만 사용하고, 서버 submit payload의 reduceOnly는 명시 플래그를 그대로 쓴다.
   */
  const effectiveReduceOnlyAtom = atom((get) =>
    isEffectiveReduceOnly({
      explicitReduceOnly: get(reduceOnlyAtom),
      isOneWay: get(isOneWayAtom),
      openPositionSide: get(currentSymbolPositionSideAtom),
      orderSide: get(sideAtom),
    }),
  );

  /**
   * Reduce-only mode: max value is bounded by the current position, not available funds.
   * - max quantity = |position.quantity|
   * - max amount (USDT) = |position.size| = |position.quantity * referencePrice|
   * - max margin (USDT) = |position.size| / leverage
   *
   * Returns `null` when reduce-only is OFF (use availableFunds-based max instead).
   */
  const reduceOnlyMaxAtom = atom<{ quantity: string; amount: string } | null>((get) => {
    if (!get(reduceOnlyAtom)) return null;
    const position = get(currentSymbolPositionAtom);
    if (!position) return { quantity: '0', amount: '0' };
    const quantity = Num(position.position).abs();
    // Use the real-time reference price so the displayed max value updates on every tick.
    const amount = quantity.multipliedBy(get(referencePriceAtom));
    return { quantity: quantity.toString(), amount: amount.toString() };
  });
  const orderPercentAtom = atom<number>((get) => {
    const symbolInfo = get(symbolInfoAtom);
    const state = get(orderStateAtom);
    const value = getKindValue(state);
    if (value === '') return 0;
    const reduceOnlyMax = get(reduceOnlyMaxAtom);
    if (reduceOnlyMax) {
      const maxByKind =
        state.kind === 'quantity'
          ? reduceOnlyMax.quantity
          : state.kind === 'amount'
            ? reduceOnlyMax.amount
            : Number(state.leverage) === 0
              ? '0'
              : Num(reduceOnlyMax.amount).div(state.leverage).toString();
      if (Num(maxByKind).isZero()) return 0;
      const rate = Num(value).div(maxByKind);
      return Math.min(Math.round(rate.times(100).toNumber()), 100);
    }
    const price = get(calculationPriceAtom);
    const availableFunds = get(availableFundsAtom);
    const quantityPrecision = symbolInfo.tradeQtyPrecision;
    const stateTo = convert(state.leverage, price, { quantityPrecision })[state.kind](value);
    return stateTo.orderPercent(availableFunds);
  });
  /** 다른 유형의 입력으로 이동하는 경우 이전 상태를 기반으로 다음 입력에 들어갈 값을 계산 */
  const transitKindAtom = atom(null, (get, set, next: 'margin' | 'quantity' | 'amount') => {
    const symbolInfo = get(symbolInfoAtom);
    const state = get(orderStateAtom);
    const { kind: prev, leverage } = state;
    if (prev === next) return;
    const price = get(calculationPriceAtom);
    const quantityPrecision = symbolInfo.tradeQtyPrecision;
    const prevTo = convert(leverage, price, { quantityPrecision })[prev](getKindValue(state));
    const kindValue = prevTo[next];
    set(orderTypeFormFieldErrorAtom, null);
    set(dirtyAtom, (d) => ({
      ...d,
      margin: false,
      quantity: false,
      amount: false,
      [next]: d[prev],
    }));
    set(orderStateAtom, {
      kind: next,
      leverage,
      [next]: kindValue,
    } as unknown as OrderState);
    // 폼 → 오더북: Margin으로 진입할 때만 orderbook을 USDT로 강제. 그 외 전환은 sync 안 함.
    if (next === 'margin') changeTradeUnit('amount');
  });

  /** 유형과 값에 따라 상태를 바꿈. 기존 유형과 다른 경우 `transitKind`을 수행 한 뒤 값을 바꿈 */
  const changeOrderStateAtom = atom(null, (get, set, kind: 'margin' | 'quantity' | 'amount', value: string) => {
    set(shouldShowBracketErrorAtom, false);
    set(orderTypeFormFieldErrorAtom, (prev) => (prev?.field === kind ? null : prev));
    set(dirtyAtom, (prev) => ({ ...prev, [kind]: true }));
    const { kind: current } = get(orderStateAtom);
    if (current != kind) set(transitKindAtom, kind);
    const sanitizedValue = value === '' ? '' : Num(value).isFinite() ? value : '0';
    set(orderStateAtom, (state) => setKindValue(state, sanitizedValue));
  });

  const changeLeverageAtom = atom(null, (_, set, leverage: number, dirty?: boolean) => {
    set(shouldShowBracketErrorAtom, false);
    set(orderTypeFormFieldErrorAtom, null);
    if (dirty) set(dirtyAtom, (prev) => ({ ...prev, leverage: true }));
    set(orderStateAtom, (state) => ({ ...state, leverage }));
  });

  /** 레버리지 변경 or 제한 적용 및 로컬 스토리지 저장
   * maxInitLeverageRestriction → position 없는 one-way 모드 유저가 leverage restriction 조건 해당될 때
   * [Leverage Restriction]: https://www.notion.so/prextech/AQXP-USDT-Bonus-Policy-Update-Product-side-28deb32ad22780868537d00569cf9c4b?source=copy_link#294eb32ad22780dea087f7466e1b5793)
   */
  const handleLeverageChangeAtom = atom(null, (get, set, value: number, dirty?: boolean) => {
    set(shouldShowBracketErrorAtom, false);

    const { isMaxInitLeverageRestricted, maxInitLeverageRestriction } = get(leverageRestrictionAtom);

    const finalLeverage = (() => {
      const hasOneWayPosition = get(hasOneWayPositionAtom);
      const positionLeverage = get(positionLeverageAtom);
      let v = value;
      if (hasOneWayPosition && positionLeverage != null) {
        v = Math.max(v, positionLeverage);
      }

      if (isMaxInitLeverageRestricted && maxInitLeverageRestriction != null) {
        return Math.min(v, maxInitLeverageRestriction);
      }
      return v;
    })();

    const isDirty = dirty ?? true;
    set(changeLeverageAtom, finalLeverage, isDirty);

    const user = get(userAtom);
    if (isDirty && user?.userId && finalLeverage !== 0) {
      const key = getFlipsterConfigsV2Key(user.userId, 'leverage');
      setLocalStorage(key, {
        ...getLocalStorage(key),
        [symbol]: finalLeverage,
      });
    }
  });

  /** `OrderTypeForm` 아래 슬라이더에 따라 바라보고 있는 입력 값을 변경 */
  const changeOrderPercentAtom = atom(null, (get, set, percent: number) => {
    set(shouldShowBracketErrorAtom, false);
    set(dirtyAtom, (prev) => ({ ...prev, orderPercent: true }));
    set(orderTypeFormFieldErrorAtom, null);
    const state = get(orderStateAtom);
    const reduceOnlyMax = get(reduceOnlyMaxAtom);

    if (reduceOnlyMax) {
      const symbolInfo = get(symbolInfoAtom);
      const quantityPrecision = symbolInfo.tradeQtyPrecision;
      switch (state.kind) {
        case 'quantity': {
          set(
            changeOrderStateAtom,
            'quantity',
            Num(reduceOnlyMax.quantity).div(100).times(percent).toFixed(quantityPrecision),
          );
          break;
        }
        case 'amount': {
          set(changeOrderStateAtom, 'amount', Num(reduceOnlyMax.amount).div(100).times(percent).toFixed(UsdtPrecision));
          break;
        }
        case 'margin': {
          set(
            changeOrderStateAtom,
            'margin',
            Number(state.leverage) === 0
              ? '0'
              : Num(reduceOnlyMax.amount).div(state.leverage).div(100).times(percent).toFixed(UsdtPrecision),
          );
          break;
        }
      }
      return;
    }

    const availableFunds = get(availableFundsAtom);
    switch (state.kind) {
      case 'quantity':
      case 'amount': {
        set(
          changeOrderStateAtom,
          'amount', // quantity의 경우 amount로 변경
          Num(availableFunds).div(100).times(percent).times(state.leverage).toFixed(UsdtPrecision),
        );
        break;
      }
      case 'margin': {
        set(changeOrderStateAtom, 'margin', Num(availableFunds).div(100).times(percent).toFixed(UsdtPrecision));
        break;
      }
    }
  });
  /** triggerTimestamp 변경 시 호출되는 atom */
  const changeTriggerTimestampAtom = atom(null, (_, set, value: Timestamp) => {
    set(dirtyAtom, (prev) => ({ ...prev, triggerTimestamp: true }));
    set(triggerTimestampAtom, value);
  });

  /** 심볼 변경 시(formBunja가 symbolBunja에 의존하여 재생성) 및 priceType 전환 시 price 초기화 */
  bunja.effect(() => {
    resetSlippage();
    const priceType = store.get(priceTypeAtom);
    if (priceType === 'limit') resetLimitPrice();
    if (priceType === 'trigger') {
      resetTriggerPrice();
      // Trigger 탭 + limit 체결 방식일 때 심볼 변경 시 order(limit) price도 새 심볼 가격으로 초기화
      if (store.get(triggerExecutionTypeAtom) === 'limit') resetLimitPrice();
    }
    return store.sub(priceTypeAtom, () => {
      const priceType = store.get(priceTypeAtom);
      if (priceType !== 'trigger') {
        store.set(triggerTypeAtom, 'price');
        // Trigger 탭을 떠나면 execution type 초기화
        store.set(triggerExecutionTypeAtom, 'market');
      }
      if (priceType === 'limit') resetLimitPrice();
      if (priceType === 'trigger') resetTriggerPrice();
    });
  });

  /** Trigger 탭 안에서 triggerType / executionType 변경 시 limit price 초기화 */
  bunja.effect(() => {
    return store.sub(triggerExecutionTypeAtom, () => {
      const executionType = store.get(triggerExecutionTypeAtom);
      if (executionType === 'limit') resetLimitPrice();
    });
  });

  /** Trigger time으로 전환 시 execution type은 market으로 강제 (time-trigger는 Market만 지원) */
  bunja.effect(() => {
    return store.sub(triggerTypeAtom, () => {
      if (store.get(triggerTypeAtom) === 'time') {
        store.set(triggerExecutionTypeAtom, 'market');
      }
    });
  });

  /**
   * 포지션 모드(One-way ↔ Multi) 전환 시 설정해 둔 TP/SL을 초기화한다. (QA-20888)
   * 모드 전환은 청산가·주문 동작 전반을 바꾸므로 레버리지 변경과 동일하게 재입력을 유도한다.
   * 직전 모드를 보관해 실제 전환(prev ≠ next)일 때만 초기화한다(판정은 순수함수에 위임).
   */
  bunja.effect(() => {
    let prevIsOneWay = store.get(isOneWayAtom);
    return store.sub(isOneWayAtom, () => {
      const nextIsOneWay = store.get(isOneWayAtom);
      if (shouldResetTpSlOnPositionModeChange({ prevIsOneWay, nextIsOneWay })) {
        store.set(resetTpSlAtom);
      }
      prevIsOneWay = nextIsOneWay;
    });
  });

  /**
   * 오더북 → 폼 동기화.
   * 폼이 margin일 때만 sync 활성. orderbook이 'qty'로 바뀌면 폼을 Position size qty로 전환.
   * 폼이 Position size(quantity/amount)이면 sync 비활성: orderbook 변경이 폼에 영향 주지 않음.
   * 폼 → 오더북(margin 진입 시 USDT 강제)은 transitKindAtom 안에서 처리.
   */
  bunja.effect(() => {
    return store.sub(tradeUnitAtom, () => {
      const mode = store.get(tradeUnitAtom);
      const { kind } = store.get(orderStateAtom);
      if (kind === 'margin' && mode === 'qty') {
        store.set(transitKindAtom, 'quantity');
      }
    });
  });

  bunja.effect(() => {
    const initLeverage = () => {
      const user = store.get(userAtom);
      if (!user?.userId) return;
      // v2 캐시를 읽기 전에 레거시 v1 캐시를 v2로 1회성 마이그레이션한다. (멱등, v2 존재 시 no-op)
      // setLocalStorage는 같은 탭에서 storage 이벤트를 발생시키지 않으므로, 반드시 read 이전에 동기 실행해야 한다.
      migrateTradeDefaultsCache(user.userId);
      const positionLeverage = store.get(positionLeverageAtom);
      const key = getFlipsterConfigsV2Key(user.userId, 'leverage');
      const storedLeverage = getLocalStorage(key)?.[symbol] ?? user.defaultLeverage ?? InitValues.leverage;
      let initialLeverage = positionLeverage ?? storedLeverage;
      /** mode-aware max(Isolated/Cross)가 변하는 경우가 있어서 최대 레버리지 제한을 적용 */
      const modeMaxLeverage = store.get(modeMaxLeverageAtom);
      if (modeMaxLeverage != null) {
        initialLeverage = Math.min(initialLeverage, modeMaxLeverage);
      }
      store.set(handleLeverageChangeAtom, initialLeverage, false);
    };
    initLeverage();
    const unsubUserAtom = store.sub(userAtom, initLeverage);
    const unsubPositionLeverageAtom = store.sub(positionLeverageAtom, initLeverage);

    return () => {
      unsubUserAtom();
      unsubPositionLeverageAtom();
    };
  });

  // mode max(bracket / isolated cap / margin mode)가 낮아지면 chip leverage를 clamp한다.
  // 포지션이 있으면 그 leverage를 유지(조정 안 함), 포지션이 없으면 min(현재 selected, modeMax)로 낮춘다.
  // (modeMaxLeverageAtom만 구독해 가격 틱 재실행을 피한다 — marginType 변경도 포함)
  bunja.effect(() =>
    store.sub(modeMaxLeverageAtom, () => {
      const nextLeverage = clampFormLeverageOnMaxChange({
        positionLeverage: store.get(positionLeverageAtom),
        currentLeverage: store.get(orderStateAtom).leverage,
        maxLeverage: store.get(modeMaxLeverageAtom),
      });
      if (nextLeverage != null) store.set(handleLeverageChangeAtom, nextLeverage, false);
    }),
  );

  return {
    // global states (symbol, account)
    sideAtom,
    availableFundsAtom,
    // order state
    priceTypeAtom,
    triggerTypeAtom,
    orderStateAtom,
    leverageAtom,
    kindAtom,
    transitKindAtom,
    changeOrderStateAtom,
    changeLeverageAtom,
    handleLeverageChangeAtom,
    changeOrderPercentAtom,
    changeTriggerTimestampAtom,
    // errors
    dirtyAtom,
    orderTypeFormFieldErrorAtom,
    checkValidationAll,
    recentOrderErrorsAtom,
    resetDirty,
    // tp/sl
    tpSlAtom,
    updateTpSlAtom,
    tpslAllowStatusAtom,
    isOrderPriceOrAmountEmptyAtom,
    advancedTpSlPresetAtom,
    commitAdvancedTpSlAtom,
    clearAdvancedTpSlAtom,
    isAdvancedTpSlConfiguredAtom,
    focusedTpSlFieldAtom,
    tpslEstPnlAtom,
    tpSlAutofillSignalAtom,
    autofillTpSlAtom,
    // Reduce Only
    reduceOnlyAtom,
    effectiveReduceOnlyAtom,
    reduceOnlyMaxAtom,
    // Max slippage (ERD-233)
    slippageEnabledAtom,
    slippageModeAtom,
    slippageValueAtom,
    resetSlippage,
    // price
    referencePriceAtom,
    triggerPriceAtom,
    triggerPriceTypeAtom,
    triggerReferencePriceAtom,
    triggerTimestampAtom,
    resetTriggerPrice,
    resetTriggerTimestampAtom,
    handleTriggerPricePercentAtom,
    orderPercentAtom,
    limitPriceAtom,
    isBboAtom,
    resetLimitPrice,
    calculationPriceAtom,
    // trigger execution type (market | limit)
    triggerExecutionTypeAtom,
    // position bracket
    shouldShowBracketErrorAtom,
  };
});
formBunja.debugLabel = 'formBunja';

interface ConvertOptions {
  quantityPrecision?: number;
}
export function convert(
  leverage: number,
  price: string,
  options?: ConvertOptions,
): Record<
  'quantity' | 'margin' | 'amount',
  (value: string) => {
    margin: string;
    quantity: string;
    amount: string;
    orderPercent: (availableFunds: string) => number;
  }
> {
  const { quantityPrecision = 2 } = options ?? {};
  return {
    quantity(quantity: string) {
      return {
        margin: calcQtyToMargin(quantity, leverage, price),
        quantity: Num(quantity).toFixed(quantityPrecision),
        amount: calcQtyToAmount(quantity, price),
        orderPercent(availableFunds: string) {
          if (Num(price).isZero() || Number(leverage) === 0 || Num(availableFunds).isZero()) return 0;
          const maxQuantity = Num(availableFunds).times(leverage).div(price).toFixed(quantityPrecision);
          if (Num(maxQuantity).isZero()) return 0;
          const rate = Num(quantity).div(maxQuantity);
          return Math.min(Math.round(rate.times(100).toNumber()), 100);
        },
      };
    },
    margin(margin: string) {
      return {
        margin: Num(margin).toFixed(UsdtPrecision),
        quantity: calcMarginToQty(margin, leverage, price, quantityPrecision),
        amount: calcMarginToAmount(margin, leverage),
        orderPercent(availableFunds: string) {
          if (Num(availableFunds).isZero()) return 0;
          const rate = Num(margin).div(availableFunds); // prob clamp
          return Math.min(Math.round(rate.times(100).toNumber()), 100);
        },
      };
    },
    amount(amount: string) {
      return {
        margin: calcAmountToMargin(amount, leverage),
        quantity: calcAmountToQty(amount, price, quantityPrecision),
        amount: Num(amount).toFixed(UsdtPrecision),
        orderPercent(availableFunds: string) {
          if (Num(availableFunds).isZero() || Number(leverage) === 0) return 0;
          const rate = Num(amount).div(leverage).div(availableFunds); // prob clamp
          return Math.min(Math.round(rate.times(100).toNumber()), 100);
        },
      };
    },
  };
}
/**
 * Advanced TP/SL을 설정하려면 주문의 price/amount가 채워져 있어야 한다.
 * 비어 있으면 `true`를 반환하며, 이 경우 Advanced 버튼 클릭 시 toast 에러로 진입을 막는다.
 * - amount: 현재 입력 탭(margin/quantity/amount)의 값이 0 이하이면 비어 있는 것으로 본다.
 * - price: 주문 타입별 실제 입력 필드 기준.
 *   - market: 가격 입력 필드가 없으므로 항상 채워진 것으로 본다.
 *   - limit: BBO가 아니면 limit price 필요.
 *   - trigger(price): trigger price 필요. 실행 방식이 limit이고 BBO가 아니면 limit price도 필요.
 *   - trigger(time): 가격 입력 필드가 없으므로 채워진 것으로 본다.
 */
export function isOrderPriceOrAmountEmpty(params: {
  orderState: OrderState;
  priceType: PriceType;
  triggerType: 'price' | 'time';
  triggerExecutionType: TriggerExecutionType;
  isBbo: boolean;
  limitPrice?: string;
  triggerPrice?: string;
}): boolean {
  const { orderState, priceType, triggerType, triggerExecutionType, isBbo, limitPrice, triggerPrice } = params;

  if (Num(getKindValue(orderState)).isLessThanOrEqualTo(0)) return true;

  switch (priceType) {
    case 'market':
      return false;
    case 'limit':
      return !isBbo && Number(limitPrice || 0) === 0;
    case 'trigger': {
      if (triggerType === 'time') return false;
      if (Number(triggerPrice || 0) === 0) return true;
      if (triggerExecutionType === 'limit' && !isBbo) {
        return Number(limitPrice || 0) === 0;
      }
      return false;
    }
    default:
      return false;
  }
}

export function getKindValue(state: OrderState) {
  switch (state.kind) {
    case 'amount':
      return state.amount || '0';
    case 'margin':
      return state.margin || '0';
    case 'quantity':
      return state.quantity || '0';
  }
}
function setKindValue(state: OrderState, value: string) {
  switch (state.kind) {
    case 'amount':
      return { ...state, amount: value };
    case 'margin':
      return { ...state, margin: value };
    case 'quantity':
      return { ...state, quantity: value };
  }
}
